import React, { useEffect, useState } from 'react';
import { normalizeTagName, pickTagColor, TAG_COLOR_PALETTE, type TagResponse } from '@troia-v3/types';

import { Button, Drawer, Input, Textarea } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { useCreateTag, useUpdateTag } from '../hooks/useTags';

interface TagFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  tag?: TagResponse | null;
}

/**
 * Drawer de criação/edição de tag.
 *
 * Duas coisas que o usuário precisa ver ANTES de salvar, e que por isso são
 * parte do formulário, não da mensagem de erro:
 *
 * - **A prévia do chip**, porque a cor é o único campo cujo efeito não se
 *   deduz do valor — `#0E7490` não diz nada, o chip diz.
 * - **O aviso de cascata**, quando o nome muda numa tag que já existe. Quem
 *   renomeia precisa saber que a mudança alcança os contatos, tickets e
 *   clientes já marcados; é o comportamento certo, mas é surpreendente se
 *   ninguém avisar.
 */
export const TagFormDrawer: React.FC<TagFormDrawerProps> = ({ isOpen, onClose, tag }) => {
  const isEdit = !!tag;
  const createMut = useCreateTag();
  const updateMut = useUpdateTag();

  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(TAG_COLOR_PALETTE[0] as string);
  const [corEscolhidaAMao, setCorEscolhidaAMao] = useState(false);
  const [description, setDescription] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName(tag?.name ?? '');
    setColor(tag?.color ?? (TAG_COLOR_PALETTE[0] as string));
    setCorEscolhidaAMao(!!tag);
    setDescription(tag?.description ?? '');
    setFormError(null);
  }, [isOpen, tag]);

  /**
   * Enquanto ninguém escolheu uma cor, ela acompanha o nome — a MESMA cor que
   * o catálogo atribuiria sozinho (`pickTagColor`). Assim a prévia não mente:
   * o chip que a pessoa vê é o chip que vai existir.
   */
  const handleNameChange = (valor: string): void => {
    setName(valor);
    if (!corEscolhidaAMao) setColor(pickTagColor(valor.trim()));
  };

  const nomeLimpo = name.replace(/\s+/g, ' ').trim();
  const nomeValido = normalizeTagName(nomeLimpo).length > 0;
  const renomeando = isEdit && nomeLimpo !== tag?.name;
  const salvando = createMut.isPending || updateMut.isPending;

  const handleSubmit = async (): Promise<void> => {
    if (!nomeValido) {
      setFormError('Informe um nome com ao menos uma letra ou número.');
      return;
    }
    setFormError(null);

    try {
      if (isEdit) {
        await updateMut.mutateAsync({
          id: tag.id,
          data: {
            name: nomeLimpo,
            color,
            // `null` LIMPA a descrição; string vazia não limparia nada.
            description: description.trim() === '' ? null : description.trim(),
          },
        });
      } else {
        await createMut.mutateAsync({
          name: nomeLimpo,
          color,
          ...(description.trim() !== '' ? { description: description.trim() } : {}),
        });
      }
      onClose();
    } catch {
      // O toast do hook já mostrou o motivo (inclusive o 409 de nome duplicado).
    }
  };

  const footer = (
    <div className="flex items-center justify-end gap-3 w-full">
      <Button variant="secondary" onClick={onClose} disabled={salvando} data-testid="tag-form-cancel">
        Cancelar
      </Button>
      <Button
        variant="primary"
        onClick={() => void handleSubmit()}
        disabled={salvando || !nomeValido}
        data-testid="tag-form-save"
      >
        {salvando ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar tag'}
      </Button>
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      icon={<Icon name="tag" className="h-5 w-5" />}
      title={isEdit ? 'Editar tag' : 'Nova tag'}
      subtitle={isEdit ? tag.name : 'Disponível para contatos, tickets, clientes e conversas'}
      footer={footer}
      disabled={salvando}
    >
      <div className="px-6 py-5 space-y-5" data-testid="tag-form">
        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: '#475569' }}>
            Nome <span className="text-red-500 text-[10px]">*</span>
          </label>
          <Input
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Ex: Cliente VIP"
            maxLength={60}
            autoFocus
            data-testid="tag-form-name"
          />
          <p className="text-[10px] mt-1" style={{ color: '#94a3b8' }}>
            Tags iguais a menos de acento ou maiúsculas são recusadas — &quot;VIP&quot; e &quot;vip&quot; são a mesma tag.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: '#475569' }}>Cor</label>
          <div className="flex flex-wrap gap-2" data-testid="tag-form-colors">
            {TAG_COLOR_PALETTE.map((hex) => {
              const selecionada = hex.toUpperCase() === color.toUpperCase();
              return (
                <button
                  key={hex}
                  type="button"
                  onClick={() => {
                    setColor(hex);
                    setCorEscolhidaAMao(true);
                  }}
                  aria-label={`Cor ${hex}`}
                  aria-pressed={selecionada}
                  data-testid={`tag-form-color-${hex.replace('#', '')}`}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-transform"
                  style={{
                    backgroundColor: hex,
                    outline: selecionada ? `2px solid ${hex}` : 'none',
                    outlineOffset: 2,
                  }}
                >
                  {selecionada && <Icon name="check" className="w-4 h-4" style={{ color: '#ffffff' }} />}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: '#475569' }}>Prévia</label>
          <span
            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
            style={{ backgroundColor: `${color}1A`, color, borderColor: `${color}33` }}
            data-testid="tag-form-preview"
          >
            {nomeLimpo || 'Nome da tag'}
          </span>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: '#475569' }}>
            Descrição <span className="font-normal" style={{ color: '#94a3b8' }}>(opcional)</span>
          </label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Quando usar esta tag..."
            rows={3}
            maxLength={500}
            data-testid="tag-form-description"
          />
        </div>

        {renomeando && (
          <div
            className="rounded-lg p-3 text-xs"
            style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' }}
            data-testid="tag-form-rename-warning"
          >
            <strong>Renomear alcança quem já está marcado.</strong> Contatos, tickets, clientes e
            conversas com &quot;{tag.name}&quot; passam a ter &quot;{nomeLimpo}&quot;. É o comportamento
            certo — sem isso os filtros parariam de encontrá-los.
          </div>
        )}

        {formError && (
          <p className="text-xs" style={{ color: '#dc2626' }} data-testid="tag-form-error">
            {formError}
          </p>
        )}
      </div>
    </Drawer>
  );
};

export default TagFormDrawer;
